/**
 * Custom Error Classes for Chat Operations
 *
 * Comprehensive error handling system for sidebar chat functionality
 * following existing error patterns in the project.
 */

export interface ErrorContext {
  userId?: string;
  threadId?: string;
  messageId?: string;
  timestamp: number;
  userAgent?: string;
  url?: string;
  additionalData?: Record<string, any>;
}

export interface ErrorRecoveryOptions {
  canRetry: boolean;
  retryDelay?: number;
  maxRetries?: number;
  fallbackAction?: string;
  userMessage?: string;
}

/**
 * Base Chat Error Class
 * Extends Error with additional context and recovery options
 */
export class ChatError extends Error {
  public readonly code: string;
  public readonly context: ErrorContext;
  public readonly recovery: ErrorRecoveryOptions;
  public readonly severity: "low" | "medium" | "high" | "critical";
  public readonly timestamp: number;

  constructor(
    message: string,
    code: string,
    context: Partial<ErrorContext> = {},
    recovery: Partial<ErrorRecoveryOptions> = {},
    severity: "low" | "medium" | "high" | "critical" = "medium"
  ) {
    super(message);
    this.name = "ChatError";
    this.code = code;
    this.timestamp = Date.now();
    this.severity = severity;

    this.context = {
      timestamp: this.timestamp,
      ...context,
    };

    this.recovery = {
      canRetry: false,
      userMessage: "An error occurred. Please try again.",
      ...recovery,
    };

    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ChatError);
    }
  }

  /**
   * Convert error to JSON for logging
   */
  toJSON() {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      severity: this.severity,
      timestamp: this.timestamp,
      context: this.context,
      recovery: this.recovery,
      stack: this.stack,
    };
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    return this.recovery.userMessage || this.message;
  }

  /**
   * Check if error is retryable
   */
  isRetryable(): boolean {
    return this.recovery.canRetry;
  }
}

/**
 * Authentication Error
 * For authentication and authorization failures
 */
export class ChatAuthenticationError extends ChatError {
  constructor(
    message: string = "Authentication required",
    context: Partial<ErrorContext> = {},
    recovery: Partial<ErrorRecoveryOptions> = {}
  ) {
    super(
      message,
      "CHAT_AUTH_ERROR",
      context,
      {
        canRetry: false,
        userMessage: "Please sign in to continue chatting.",
        fallbackAction: "redirect_to_login",
        ...recovery,
      },
      "high"
    );
    this.name = "ChatAuthenticationError";
  }
}

/**
 * Rate Limit Error
 * For rate limiting and throttling violations
 */
export class ChatRateLimitError extends ChatError {
  public readonly retryAfter: number;

  constructor(
    message: string = "Rate limit exceeded",
    retryAfter: number = 60,
    context: Partial<ErrorContext> = {},
    recovery: Partial<ErrorRecoveryOptions> = {}
  ) {
    super(
      message,
      "CHAT_RATE_LIMIT",
      context,
      {
        canRetry: true,
        retryDelay: retryAfter * 1000,
        maxRetries: 3,
        userMessage: `Too many requests. Please wait ${retryAfter} seconds before trying again.`,
        ...recovery,
      },
      "medium"
    );
    this.name = "ChatRateLimitError";
    this.retryAfter = retryAfter;
  }
}

/**
 * Streaming Error
 * For streaming-related failures
 */
export class ChatStreamingError extends ChatError {
  public readonly streamId?: string;
  public readonly streamPhase: "initialization" | "streaming" | "completion" | "cleanup";

  constructor(
    message: string,
    streamPhase: "initialization" | "streaming" | "completion" | "cleanup",
    streamId?: string,
    context: Partial<ErrorContext> = {},
    recovery: Partial<ErrorRecoveryOptions> = {}
  ) {
    super(
      message,
      "CHAT_STREAMING_ERROR",
      { ...context, additionalData: { streamId, streamPhase, ...context.additionalData } },
      {
        canRetry: streamPhase === "initialization" || streamPhase === "streaming",
        retryDelay: 2000,
        maxRetries: 2,
        userMessage: "Connection issue occurred. Retrying...",
        fallbackAction: "fallback_to_non_streaming",
        ...recovery,
      },
      "medium"
    );
    this.name = "ChatStreamingError";
    this.streamId = streamId;
    this.streamPhase = streamPhase;
  }
}

/**
 * Validation Error
 * For input validation failures
 */
export class ChatValidationError extends ChatError {
  public readonly field?: string;
  public readonly validationRule?: string;

  constructor(
    message: string,
    field?: string,
    validationRule?: string,
    context: Partial<ErrorContext> = {},
    recovery: Partial<ErrorRecoveryOptions> = {}
  ) {
    super(
      message,
      "CHAT_VALIDATION_ERROR",
      { ...context, additionalData: { field, validationRule, ...context.additionalData } },
      {
        canRetry: false,
        userMessage: message,
        ...recovery,
      },
      "low"
    );
    this.name = "ChatValidationError";
    this.field = field;
    this.validationRule = validationRule;
  }
}

/**
 * Network Error
 * For network and connectivity issues
 */
export class ChatNetworkError extends ChatError {
  public readonly statusCode?: number;
  public readonly endpoint?: string;

  constructor(
    message: string,
    statusCode?: number,
    endpoint?: string,
    context: Partial<ErrorContext> = {},
    recovery: Partial<ErrorRecoveryOptions> = {}
  ) {
    super(
      message,
      "CHAT_NETWORK_ERROR",
      { ...context, additionalData: { statusCode, endpoint, ...context.additionalData } },
      {
        canRetry: true,
        retryDelay: 1000,
        maxRetries: 3,
        userMessage: "Connection issue. Retrying...",
        ...recovery,
      },
      statusCode && statusCode >= 500 ? "high" : "medium"
    );
    this.name = "ChatNetworkError";
    this.statusCode = statusCode;
    this.endpoint = endpoint;
  }
}

/**
 * Thread Error
 * For thread management issues
 */
export class ChatThreadError extends ChatError {
  public readonly threadOperation: "create" | "continue" | "access" | "delete";

  constructor(
    message: string,
    threadOperation: "create" | "continue" | "access" | "delete",
    context: Partial<ErrorContext> = {},
    recovery: Partial<ErrorRecoveryOptions> = {}
  ) {
    super(
      message,
      "CHAT_THREAD_ERROR",
      { ...context, additionalData: { threadOperation, ...context.additionalData } },
      {
        canRetry: threadOperation === "create" || threadOperation === "continue",
        retryDelay: 1500,
        maxRetries: 2,
        userMessage:
          threadOperation === "access"
            ? "Unable to access this conversation."
            : "Unable to process your message. Please try again.",
        ...recovery,
      },
      threadOperation === "access" ? "high" : "medium"
    );
    this.name = "ChatThreadError";
    this.threadOperation = threadOperation;
  }
}

/**
 * Error Factory Functions
 * Convenient functions for creating common error types
 */
export const ChatErrorFactory = {
  authentication: (message?: string, context?: Partial<ErrorContext>) =>
    new ChatAuthenticationError(message, context),

  rateLimit: (retryAfter?: number, context?: Partial<ErrorContext>) =>
    new ChatRateLimitError(undefined, retryAfter, context),

  streaming: (
    message: string,
    phase: "initialization" | "streaming" | "completion" | "cleanup",
    streamId?: string,
    context?: Partial<ErrorContext>
  ) => new ChatStreamingError(message, phase, streamId, context),

  validation: (message: string, field?: string, rule?: string, context?: Partial<ErrorContext>) =>
    new ChatValidationError(message, field, rule, context),

  network: (
    message: string,
    statusCode?: number,
    endpoint?: string,
    context?: Partial<ErrorContext>
  ) => new ChatNetworkError(message, statusCode, endpoint, context),

  thread: (
    message: string,
    operation: "create" | "continue" | "access" | "delete",
    context?: Partial<ErrorContext>
  ) => new ChatThreadError(message, operation, context),

  generic: (message: string, code: string, context?: Partial<ErrorContext>) =>
    new ChatError(message, code, context),
};

/**
 * Error Type Guards
 * Helper functions to check error types
 */
export const isChatError = (error: any): error is ChatError => error instanceof ChatError;

export const isChatAuthenticationError = (error: any): error is ChatAuthenticationError =>
  error instanceof ChatAuthenticationError;

export const isChatRateLimitError = (error: any): error is ChatRateLimitError =>
  error instanceof ChatRateLimitError;

export const isChatStreamingError = (error: any): error is ChatStreamingError =>
  error instanceof ChatStreamingError;

export const isChatValidationError = (error: any): error is ChatValidationError =>
  error instanceof ChatValidationError;

export const isChatNetworkError = (error: any): error is ChatNetworkError =>
  error instanceof ChatNetworkError;

export const isChatThreadError = (error: any): error is ChatThreadError =>
  error instanceof ChatThreadError;
